import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ChevronLeft, ChevronRight, Search } from "lucide-react";
import { API } from "@/lib/api";


export default function CustomersPage() {
  const { authHeaders, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [vendorFilter, setVendorFilter] = useState("all");
  const [loading, setLoading] = useState(true);

  const load = (p = page, q = search, vf = vendorFilter) => {
    let url = `${API}/customers?page=${p}&limit=20`;
    if (q) url += `&search=${encodeURIComponent(q)}`;
    if (vf && vf !== "all") url += `&client_id=${vf}`;
    setLoading(true);
    axios.get(url, { headers: authHeaders() }).then((r) => {
      setCustomers(r.data.customers || []);
      setTotal(r.data.total || 0);
    }).finally(() => setLoading(false));
  };

  useEffect(() => {
    if (isAdmin) {
      axios.get(`${API}/vendors`, { headers: authHeaders() }).then((r) => setVendors(r.data)).catch(() => {});
    }
    load();
  }, []);

  const totalPages = Math.ceil(total / 20);

  return (
    <div className="space-y-6" data-testid="customers-page">
      <div>
        <h1 className="text-2xl font-bold text-ink tracking-tight">Customers</h1>
        <p className="text-sm text-ink/50 mt-0.5">{total.toLocaleString()} total customers</p>
      </div>

      <div className="flex gap-3 flex-wrap">
        <div className="relative w-64">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink/40" />
          <Input
            placeholder="Search email, mobile, name..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") { setPage(1); load(1, search, vendorFilter); } }}
            className="pl-9 h-9 text-sm"
            data-testid="customer-search-input"
          />
        </div>
        {isAdmin && (
          <Select value={vendorFilter} onValueChange={(v) => { setVendorFilter(v); setPage(1); load(1, search, v); }}>
            <SelectTrigger className="w-44 h-9 text-sm" data-testid="vendor-filter">
              <SelectValue placeholder="All Vendors" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Vendors</SelectItem>
              {vendors.map((v) => <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
        <Button variant="outline" size="sm" onClick={() => { setPage(1); load(1, search, vendorFilter); }} className="h-9 text-sm" data-testid="search-button">
          Search
        </Button>
      </div>

      <div className="bg-white rounded-md border border-ink/10 overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-surface">
              <TableHead className="text-xs uppercase tracking-widest text-ink/50 font-semibold">Customer</TableHead>
              {isAdmin && <TableHead className="text-xs uppercase tracking-widest text-ink/50 font-semibold">Vendor</TableHead>}
              <TableHead className="text-xs uppercase tracking-widest text-ink/50 font-semibold">Balance</TableHead>
              <TableHead className="text-xs uppercase tracking-widest text-ink/50 font-semibold">Lifetime</TableHead>
              <TableHead className="text-xs uppercase tracking-widest text-ink/50 font-semibold">Tier</TableHead>
              <TableHead className="text-xs uppercase tracking-widest text-ink/50 font-semibold">Profile</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={isAdmin ? 6 : 5} className="text-center py-12 text-ink/40 text-sm">Loading...</TableCell></TableRow>
            ) : customers.length === 0 ? (
              <TableRow><TableCell colSpan={isAdmin ? 6 : 5} className="text-center py-12 text-ink/40 text-sm">No customers found</TableCell></TableRow>
            ) : (
              customers.map((c) => (
                <TableRow
                  key={c.id}
                  className="cursor-pointer hover:bg-surface transition-colors"
                  onClick={() => navigate(`/customers/${c.id}`)}
                  data-testid={`customer-row-${c.id}`}
                >
                  <TableCell>
                    <div>
                      <p className="text-sm font-medium text-ink">{c.name || "—"}</p>
                      <p className="text-xs text-ink/40">{c.email || c.mobile_number}</p>
                    </div>
                  </TableCell>
                  {isAdmin && <TableCell className="text-sm text-ink/60">{c.vendor_name || "—"}</TableCell>}
                  <TableCell className="font-mono text-sm font-semibold text-ink">{c.point_balance?.toLocaleString()}</TableCell>
                  <TableCell className="font-mono text-sm text-ink/50">{c.total_points_earned?.toLocaleString()}</TableCell>
                  <TableCell>
                    {c.tier ? (
                      <Badge variant="outline" className="text-xs">{c.tier}</Badge>
                    ) : (
                      <span className="text-xs text-ink/40">—</span>
                    )}
                  </TableCell>
                  <TableCell>
                    {c.is_shadow_profile ? (
                      <Badge variant="outline" className="text-xs bg-surface text-ink/50">Shadow</Badge>
                    ) : (
                      <Badge variant="outline" className="text-xs bg-purple-100 text-success border-success/30">Verified</Badge>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {totalPages > 1 && (
        <div className="flex items-center justify-between text-sm text-ink/50">
          <span>Page {page} of {totalPages}</span>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" disabled={page === 1} onClick={() => { setPage(page - 1); load(page - 1); }} data-testid="prev-page">
              <ChevronLeft size={14} />
            </Button>
            <Button variant="outline" size="sm" disabled={page === totalPages} onClick={() => { setPage(page + 1); load(page + 1); }} data-testid="next-page">
              <ChevronRight size={14} />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
